// =============================================================================
// domain/goals.ts — period targets, and the windows they measure over.
// -----------------------------------------------------------------------------
// Relocated from the old client's `computeGoalProgress` and the dashboard's
// goals loop. Three things here are easy to get wrong and expensive to notice,
// so they are stated before the code:
//
// ── 🚨 1. A GOAL IGNORES THE GLOBAL DATE FILTER, ON PURPOSE ──
//
// The old client says so outright: "goals carry their own period and shouldn't
// intersect with the global date filter — a daily $ target needs to measure
// today's P&L even when the user has filtered the journal to last month." So
// this is the one surface on the dashboard that reads OUTSIDE the active range,
// and a yearly goal reads the whole year no matter what the filter says.
//
// ── 🚨 2. THE GOAL WEEK STARTS ON SUNDAY. THE RANGE FILTER'S STARTS ON MONDAY ──
//
// `startOfWeek` in the old client is `x.setDate(x.getDate() - x.getDay())`, and
// `getDay()` counts Sunday as 0. The global range filter's `week` preset is
// Monday-based (and in session mode stops at Friday). They are different
// windows and both are deliberate.
//
// ⚠ Reusing `rangeBounds('week')` here would look obviously right and be wrong
// by a day at each end. It would also AGREE with beta on most weeks — a futures
// trader's Sunday and Saturday are usually empty — so a side-by-side would
// confirm it. Checked on Josh's book the day this was written: the two windows
// returned the identical total, because no trade in that week had an effective
// date of Sunday or Saturday. That is a coincidence, not a verification.
//
// ── 🚨 3. THE WINDOW IS ANCHORED TO THE WALL CLOCK, NOT THE SESSION DAY ──
//
// `computeGoalProgress` opens with `const today = new Date()`, while the trades
// it filters are compared on their EFFECTIVE (session) date. So a `day` goal on
// a Sunday evening measures the Sunday window against trades already stamped
// Monday, and reads zero.
//
// That is odd and it is beta's, so it is what this does. The journal anchors to
// the session day because beta's journal does (D86/D87); goals anchor to the
// wall clock because beta's goals do. "Which anchor" is a per-surface question
// exactly like "which population" (D77), and guessing consistently is how a port
// diverges consistently.
// =============================================================================

import { effectiveTradeDate, type DatedTrade } from './session';
import type { DayBoundaryMode } from './range';

export type GoalPeriod = 'day' | 'week' | 'month' | 'year' | 'all';

/** The four templates beta's goal form can create. */
export type GoalTemplate = 'dollar-pl' | 'boolean-adherence' | 'tag-cap' | 'streak';

export interface Goal {
  id: string;
  name: string;
  template: string;
  period: string;
  params: Record<string, unknown>;
  /** Empty = every account. Never null — the column defaults to `{}`. */
  accountIds: string[];
}

export interface GoalWindow {
  /** Inclusive ISO date. */
  start: string;
  /** Inclusive ISO date. */
  end: string;
}

// ---------------------------------------------------------------------------
// The window
// ---------------------------------------------------------------------------

const iso = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

/** Parse an ISO date as UTC midnight, so arithmetic never crosses a DST seam. */
const parse = (isoDate: string): Date => new Date(`${isoDate}T00:00:00Z`);

/**
 * The span a goal measures over, both bounds inclusive.
 *
 * @param todayIso the WALL-CLOCK date. See note 3 in the header — this is not
 *   the session anchor, and the difference is beta's.
 */
export function goalWindow(period: string, todayIso: string): GoalWindow {
  const d = parse(todayIso);

  switch (period) {
    case 'day':
      return { start: todayIso, end: todayIso };

    case 'week': {
      // ⚠ SUNDAY-BASED. `getUTCDay()` is 0 on Sunday, which is exactly what the
      // old client's `- x.getDay()` does. See note 2 — this is NOT the range
      // filter's week.
      const start = new Date(d);
      start.setUTCDate(start.getUTCDate() - start.getUTCDay());
      const end = new Date(start);
      end.setUTCDate(end.getUTCDate() + 6);
      return { start: iso(start), end: iso(end) };
    }

    case 'month': {
      const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
      // Day 0 of the NEXT month is the last day of this one — no month-length table.
      const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
      return { start: iso(start), end: iso(end) };
    }

    case 'year':
      return { start: `${d.getUTCFullYear()}-01-01`, end: `${d.getUTCFullYear()}-12-31` };

    case 'all':
    default:
      // ⚠ Ends TODAY, not at the end of time. The old client uses `new Date()`
      // for the upper bound here, so an all-time goal does not count a trade
      // dated in the future — which imports do produce on a timezone slip.
      return { start: '1970-01-01', end: todayIso };
  }
}

/**
 * The earliest start across a set of goals — how far back a read has to reach
 * to serve all of them.
 *
 * ⚠ Returns null for an empty list, which the caller must treat as "no read
 * needed" rather than as "read everything".
 */
export function earliestGoalStart(goals: readonly Goal[], todayIso: string): string | null {
  let earliest: string | null = null;
  for (const g of goals) {
    const { start } = goalWindow(g.period, todayIso);
    if (earliest === null || start < earliest) earliest = start;
  }
  return earliest;
}

// ---------------------------------------------------------------------------
// Which goals show, and which trades each one counts
// ---------------------------------------------------------------------------

/**
 * Goals relevant to the active account selection.
 *
 * Beta's rule, transcribed: a global goal (no accountIds) always shows; an
 * account-tagged goal shows when ANY of its accounts is in the active filter;
 * an empty filter ("all accounts") shows everything.
 */
export function visibleGoals(
  goals: readonly Goal[],
  activeAccountIds: readonly string[],
): Goal[] {
  return goals.filter((g) => {
    const ids = Array.isArray(g.accountIds) ? g.accountIds : [];
    if (ids.length === 0) return true;
    if (activeAccountIds.length === 0) return true;
    return ids.some((id) => activeAccountIds.includes(id));
  });
}

/**
 * The account ids a goal's arithmetic runs over, or `null` for "every account".
 *
 * Four cases, and they are not symmetric — the old client spells them out:
 *
 *   goal empty + filter empty  → every account
 *   goal empty + filter set    → the filter
 *   goal set   + filter empty  → the goal's accounts
 *   both set                   → the INTERSECTION
 *
 * ⚠ An empty intersection returns an empty array, NOT null. "These two
 * selections have nothing in common" must measure zero trades; collapsing it to
 * null would silently measure all of them, which is the opposite answer.
 */
export function goalAccountScope(
  goal: Goal,
  activeAccountIds: readonly string[],
): readonly string[] | null {
  const ids = Array.isArray(goal.accountIds) ? goal.accountIds : [];
  if (ids.length === 0 && activeAccountIds.length === 0) return null;
  if (ids.length === 0) return activeAccountIds;
  if (activeAccountIds.length === 0) return ids;
  return activeAccountIds.filter((id) => ids.includes(id));
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export interface GoalTrade extends DatedTrade {
  accountId: string | null;
  netPnl: number | null;
  /**
   * ⚠ NULL IS NOT FALSE. The column defaults to true, and only an explicit
   * `false` marks a trade unreviewed — so `annotated == null` counts. Coercing
   * absent to false would silently drop rows from every adherence percentage.
   */
  annotated?: boolean | null;
  /** The six discipline flags. Absent means "did not happen" — see below. */
  booleans?: Record<string, unknown> | null;
  mistakeTags?: readonly string[] | null;
}

/** One journal day, for the two templates measured over DAYS rather than trades. */
export interface GoalJournalDay {
  date: string;
  booleans?: Record<string, unknown> | null;
}

export interface GoalProgress {
  /** The measured value. Null when this template is not computed here. */
  actual: number | null;
  target: number;
  /** Percent of target. Unclamped — the caller clamps the BAR, not the text. */
  pct: number;
  /**
   * `null` when the template is one this client does not compute. Rendering 0%
   * for those would be a claim about the trader's behaviour rather than an
   * admission about the software.
   */
  supported: boolean;
  /**
   * How to PHRASE the two numbers. Only this function knows the units — a
   * percentage, a bare count, a number of days — and the card was rendering
   * `money(actual) / money(target)` for all of them, which is right for exactly
   * one of the four templates.
   */
  label: string;
  targetLabel: string;
}

/**
 * `dollar-pl` progress: summed net P&L over the goal's own window.
 *
 * @param counts the metrics gate. Beta's `pnlInRange` opens with
 *   `if (!countsForMetrics(t)) return false`, so SIM and replay accounts do not
 *   move a target. On Josh's book that is the difference between −$28,984 and
 *   −$5,853 for one week — not a rounding matter.
 */
export function goalProgress(
  goal: Goal,
  trades: readonly GoalTrade[],
  opts: {
    todayIso: string;
    mode: DayBoundaryMode;
    counts: (t: GoalTrade) => boolean;
    activeAccountIds: readonly string[];
    /** Journal days, for the two templates measured over DAYS rather than trades. */
    journal?: readonly GoalJournalDay[];
  },
): GoalProgress {
  const { start, end } = goalWindow(goal.period, opts.todayIso);
  const scope = goalAccountScope(goal, opts.activeAccountIds);
  const scoped = scope === null ? null : new Set(scope);
  const journal = opts.journal ?? [];
  const inWindow = (d: string) => d >= start && d <= end;

  /** In this goal's account scope, inside its window, and actually reviewed. */
  const judged = (t: GoalTrade): boolean => {
    if (scoped && !scoped.has(String(t.accountId ?? ''))) return false;
    // ⚠ `=== false`, never `!t.annotated`. Absent means reviewed — see GoalTrade.
    if (t.annotated === false) return false;
    const d = effectiveTradeDate(t, opts.mode);
    return !!d && inWindow(d);
  };

  if (goal.template === 'boolean-adherence') {
    const key = String(goal.params?.['booleanKey'] ?? '');
    const threshold = Number(goal.params?.['threshold']) || 0;
    const overDays = String(goal.params?.['scope'] ?? 'trades') === 'days';

    /**
     * ── 🚨 THE FLAG BEING **TRUE** COUNTS AS PASSING, AND THAT IS BETA'S RULE ──
     *
     * Every TRADE-level key this template offers is a BREACH: "Plan not
     * followed", "Revenge trade", "Overtraded". So a goal of "≥ 90% adherence on
     * Plan not followed" scores the trades where the plan was NOT followed as
     * the passing ones — beta's arithmetic reads the metric backwards for every
     * trade-level option in its own picker.
     *
     * ▶ REPRODUCED, NOT CORRECTED. Inverting it would make this client disagree
     * with beta about a number the trader may have been reading for weeks, and
     * "adherence to Plan not followed" has no obviously right reading to invert
     * TO — the sensible fix is a POSITIVE key, which is a product decision.
     * Recorded here and on #48 so it is Josh's call rather than a silent one.
     *
     * ⚠ The DAY-level key (`preMarketPlanFollowed`) is positive, so the `days`
     * scope reads correctly today. Only the trade scope is affected.
     */
    const days = journal.filter((j) => inWindow(j.date));
    const rows = trades.filter(judged);
    const total = overDays ? days.length : rows.length;
    const passed = overDays
      ? days.filter((j) => !!j.booleans?.[key]).length
      : rows.filter((t) => !!t.booleans?.[key]).length;

    const rate = total ? (passed / total) * 100 : 0;
    return {
      actual: rate,
      target: threshold,
      pct: threshold ? (rate / threshold) * 100 : 0,
      supported: true,
      label: `${passed}/${total} (${rate.toFixed(0)}%)`,
      targetLabel: `≥ ${threshold}%`,
    };
  }

  if (goal.template === 'tag-cap') {
    const tag = String(goal.params?.['tag'] ?? '');
    const max = Number(goal.params?.['max']) || 0;
    const count = trades.filter((t) => judged(t) && (t.mistakeTags ?? []).includes(tag)).length;

    return {
      actual: count,
      target: max,
      /**
       * ── ⚠ THE BAR IS INVERTED, AND IT HAS TO BE ──
       *
       * This is a CAP: fewer is better. A full bar means "well under your
       * limit", and it empties as the count climbs. Using `count / max` like
       * every other template would FILL the bar as the trader breaks the goal —
       * solid green at the moment they are furthest from meeting it.
       *
       * Floored at 0 so exceeding the cap empties the bar rather than going
       * negative, which is beta's `Math.max(0, ...)`.
       */
      pct: max ? Math.max(0, (1 - count / Math.max(max, 1)) * 100) : 0,
      supported: true,
      label: `${count}`,
      targetLabel: `≤ ${max}`,
    };
  }

  if (goal.template === 'streak') {
    const key = String(goal.params?.['booleanKey'] ?? '');
    const want = Number(goal.params?.['days']) || 0;

    /**
     * ⚠ IGNORES THE GOAL'S WINDOW ENTIRELY, which is beta's behaviour and is
     * right: a streak is a trailing run from the most recent journal day
     * backwards. Clipping it to "this month" would reset every streak on the
     * 1st, which is the opposite of what a streak measures.
     *
     * ⚠ Counts from the NEWEST day and stops at the first miss. A day with no
     * journal entry is not a miss — it is simply not in this list — so a
     * weekend does not break a run.
     */
    const newestFirst = [...journal].sort((a, b) => b.date.localeCompare(a.date));
    let streak = 0;
    for (const j of newestFirst) {
      if (!j.booleans?.[key]) break;
      streak++;
    }

    return {
      actual: streak,
      target: want,
      pct: want ? (streak / want) * 100 : 0,
      supported: true,
      label: `${streak} days`,
      targetLabel: `${want} days`,
    };
  }

  if (goal.template !== 'dollar-pl') {
    // A template this client has never heard of — a goal written by a newer
    // build, or a hand-edited row. NAMED rather than scored, for the reason the
    // three above used to be: a 0% bar is a claim about the trader.
    return { actual: null, target: 0, pct: 0, supported: false, label: '—', targetLabel: '—' };
  }

  const target = Number(goal.params?.['amount']) || 0;

  let actual = 0;
  for (const t of trades) {
    if (scoped && !scoped.has(String(t.accountId ?? ''))) continue;
    if (!opts.counts(t)) continue;
    const d = effectiveTradeDate(t, opts.mode);
    if (!d || !inWindow(d)) continue;
    actual += Number(t.netPnl) || 0;
  }

  return {
    actual,
    target,
    // ⚠ A zero target is 0%, never a division. Beta guards the same way, and a
    // goal with no amount set is a half-finished row rather than an achieved one.
    pct: target ? (actual / target) * 100 : 0,
    supported: true,
    // ⚠ EMPTY, and the caller formats these as MONEY. Only the dollar template
    // knows its unit is dollars, and only the caller has the money formatter —
    // so this is the one template whose phrasing stays with the renderer.
    label: '',
    targetLabel: '',
  };
}

/**
 * The chip beside a goal's name.
 *
 * ⚠ Falls back to `Account` for an id that resolves to nothing rather than
 * printing the raw id — a deleted account leaves a tagged goal behind, and a
 * 12-character id in the chip reads as corruption.
 */
export function goalAccountLabel(
  goal: Goal,
  nameOf: (id: string) => string | null,
): string {
  const ids = Array.isArray(goal.accountIds) ? goal.accountIds : [];
  if (ids.length === 0) return 'All accounts';
  if (ids.length === 1) return nameOf(ids[0] as string) || 'Account';
  return `${ids.length} accounts`;
}
